import { useCallback, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';

import NewProspectModal from './sections/NewProspectModal/NewProspectModal';
import ProspectDetail from './sections/ProspectDetail/ProspectDetail';
import ProspectList from './sections/ProspectList/ProspectList';
import QuoteSuccessModal from './sections/QuoteEditSuccessModal/QuoteSuccessModal';
import { ReactComponent as FoldersIcon } from '../../assets/icons/folders.svg';
import { ReactComponent as PlusIcon } from '../../assets/icons/plus.svg';
import CustomButton from '../../shared/components/Buttons/CustomButton/CustomButton';
import EmptyState from '../../shared/components/Globals/EmptyState/EmptyState';
import PrimaryHeader from '../../shared/components/Globals/Headers/PrimaryHeader/PrimaryHeader';
import Toast from '../../shared/components/Globals/Toast/Toast';
import { CLICK_ACTION, NEW_PROSPECT } from '../../shared/constants/tagManager';
import { useProspects } from '../../shared/hooks/useProspect/useProspect';
import { useUser } from '../../shared/hooks/useUser/useUser';
import type { ICreateProspectInput } from '../../shared/interfaces/prospects';
import TagManagerService from '../../shared/services/gtm/tagManagerService';
import { TRANSLATIONS_PROSPECTS } from '../../shared/translations/prospects';
import type { StatusVariant } from '../../shared/types/globals';
import { ProspectStatus, type ProspectStatus as ProspectStatusType } from '../../shared/types/prospects';
import './Prospects.scss';

interface IQuoteEditedState {
  quoteId: number | null;
  quoteName: string;
  companyName: string;
  prospectId: number;
  action?: 'create' | 'update';
  prospectStatus?: ProspectStatus;
}

const Prospects = () => {
  const { loading: userLoading } = useUser();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    filtered,
    loading,
    hasAnyProspect,
    query,
    setQuery,
    isSearching,
    statusFilter,
    setStatusFilter,
    selected,
    setSelectedId,
    createProspect,
    requestSelectAfterRefresh,
    refresh,
    newProspectCreatedId,
    setNewProspectCreatedId
  } = useProspects();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<{
    open: boolean;
    message: string;
    variant: StatusVariant;
  }>({
    open: false,
    message: '',
    variant: 'success'
  });
  const [openQuotaModal, setopenQuotaModal] = useState<IQuoteEditedState | null>(null);
  const [pendingQuoteEdit, setPendingQuoteEdit] = useState<IQuoteEditedState | null>(null);

  const { t } = useTranslation();

  const handleOpenModal = () => {
    TagManagerService.pushEvent(CLICK_ACTION, NEW_PROSPECT);
    setIsModalOpen(true);
  };
  const handleCloseModal = () => setIsModalOpen(false);

  useEffect(() => {
    const state = location.state as { quoteEdited?: IQuoteEditedState } | null;
    if (state?.quoteEdited) {
      setPendingQuoteEdit(state.quoteEdited);
      requestSelectAfterRefresh(state.quoteEdited.prospectId);

      const isCreate = state.quoteEdited.action === 'create';
      const originalStatus = state.quoteEdited.prospectStatus;

      if (isCreate && (!originalStatus || originalStatus === ProspectStatus.PendingQuote)) {
        setStatusFilter(ProspectStatus.Quoted);
      } else if (originalStatus) {
        setStatusFilter(originalStatus);
      }

      refresh();
      navigate('.', { replace: true, state: null });
    }
  }, [location.state, navigate, requestSelectAfterRefresh, setStatusFilter, refresh]);

  useEffect(() => {
    if (!loading && pendingQuoteEdit) {
      setopenQuotaModal(pendingQuoteEdit);
      setPendingQuoteEdit(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const handleCreateProspect = useCallback(
    async (body: ICreateProspectInput) => {
      try {
        setSubmitting(true);
        await createProspect(body);
        setIsModalOpen(false);
      } catch {
        setToast({
          open: true,
          message: t(TRANSLATIONS_PROSPECTS.newProspectModal.toast.error),
          variant: 'warning'
        });
      } finally {
        setSubmitting(false);
      }
    },
    [createProspect, t]
  );

  const displayNewProspectButton = () => (
    <CustomButton variant="primary" size="l" onClick={handleOpenModal}>
      <PlusIcon className="prospects__new-prospect-icon" />
      {t(TRANSLATIONS_PROSPECTS.newProspect)}
    </CustomButton>
  );

  const handleCloseopenQuotaModal = () => setopenQuotaModal(null);
  const handleCloseToast = useCallback(() => {
    setToast((prev) => ({ ...prev, open: false }));
  }, []);

  const handleStatusUpdated = useCallback(
    (newStatus: ProspectStatusType) => {
      setStatusFilter(newStatus);
    },
    [setStatusFilter]
  );

  const handleActivationRequested = useCallback(
    (prospectId: number, companyName: string) => {
      setToast({
        open: true,
        message: t(TRANSLATIONS_PROSPECTS.detail.quoted.activationRequestedToast, { companyName }),
        variant: 'success'
      });

      setStatusFilter(ProspectStatus.PendingActivation);
      setSelectedId(prospectId);
    },
    [t, setStatusFilter, setSelectedId]
  );

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      <PrimaryHeader title={t(TRANSLATIONS_PROSPECTS.title)}>{displayNewProspectButton()}</PrimaryHeader>

      {!loading && !userLoading && hasAnyProspect === false ? (
        <EmptyState
          icon={<FoldersIcon style={{ width: 28, height: 28 }} aria-hidden="true" />}
          title={t(TRANSLATIONS_PROSPECTS.emptyState.title)}
          description={t(TRANSLATIONS_PROSPECTS.emptyState.description)}
        >
          {displayNewProspectButton()}
        </EmptyState>
      ) : (
        <div className="prospects">
          <ProspectList
            prospects={filtered}
            loading={loading}
            isSearching={isSearching}
            query={query}
            onQueryChange={setQuery}
            selectedId={selected?.id ?? null}
            onSelect={setSelectedId}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
          />
          <ProspectDetail
            prospect={selected}
            isNew={selected?.id === newProspectCreatedId}
            isNewUser={!loading && !userLoading && hasAnyProspect === false}
            statusFilter={statusFilter}
            onNewProspect={handleOpenModal}
            onNewProspectDismissed={() => setNewProspectCreatedId(null)}
            onProspectStatusUpdated={handleStatusUpdated}
            onProspectStatusUpdateError={() =>
              setToast({
                open: true,
                message: t(TRANSLATIONS_PROSPECTS.newProspectModal.toast.error),
                variant: 'warning'
              })
            }
            onActivationRequested={handleActivationRequested}
          />
        </div>
      )}

      <NewProspectModal
        open={isModalOpen}
        loading={submitting}
        onClose={handleCloseModal}
        onSubmit={handleCreateProspect}
      />

      <Toast open={toast.open} message={toast.message} variant={toast.variant} onClose={handleCloseToast} />

      {openQuotaModal && (
        <QuoteSuccessModal
          open={!!openQuotaModal}
          quoteName={openQuotaModal.quoteName}
          companyName={openQuotaModal.companyName}
          mode={openQuotaModal.action ?? 'update'}
          quoteId={openQuotaModal.quoteId}
          prospectId={openQuotaModal.prospectId}
          onClose={handleCloseopenQuotaModal}
        />
      )}
    </div>
  );
};

export default Prospects;
